import { useId } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { CLARITY_EVENTS, clarityEvent } from "@/lib/clarity";
import { PREFERRED_SCHEDULES } from "@/config/contact";
import {
  callbackFormSchema,
  type CallbackFormValues,
} from "@/lib/callbackSchema";
import { apiErrorMessage } from "@/lib/apiError";
import quoteService from "@/services/quoteService";

interface CallbackFormProps {
  /** Se llama tras un envío exitoso (p. ej. para cerrar el diálogo). */
  onSuccess?: () => void;
}

/**
 * Mini-form de "solicitar llamada" (nombre, teléfono y franja preferida).
 *
 * Se monta en el widget flotante y en la home a la vez, así que el `id` de
 * los campos se deriva de `useId()` para que no colisionen las dos instancias.
 */
const CallbackForm = ({ onSuccess }: CallbackFormProps) => {
  const formId = useId();
  const { toast } = useToast();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CallbackFormValues>({
    resolver: zodResolver(callbackFormSchema),
    defaultValues: {
      name: "",
      phone: "",
      preferredSchedule: "tarde",
      website: "",
    },
  });

  const onSubmit = async (values: CallbackFormValues) => {
    if (values.website) return;

    try {
      await quoteService.createCallback({
        name: values.name,
        phone: values.phone,
        preferredSchedule: values.preferredSchedule,
      });

      clarityEvent(CLARITY_EVENTS.CALLBACK_REQUEST);
      toast({
        title: "¡Listo, te llamamos!",
        description: "Te contactamos en la franja que elegiste.",
      });
      reset();
      onSuccess?.();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "No pudimos agendar la llamada",
        description:
          apiErrorMessage(error) ??
          "Revisá tu conexión e intentá de nuevo, o escribinos por WhatsApp.",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
      <div className="space-y-1.5">
        <Label htmlFor={`${formId}-name`}>Nombre</Label>
        <Input
          id={`${formId}-name`}
          autoComplete="name"
          aria-invalid={Boolean(errors.name)}
          {...register("name")}
        />
        {errors.name && (
          <p className="text-sm text-red-600">{errors.name.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${formId}-phone`}>Teléfono</Label>
        <Input
          id={`${formId}-phone`}
          type="tel"
          autoComplete="tel"
          aria-invalid={Boolean(errors.phone)}
          {...register("phone")}
        />
        {errors.phone && (
          <p className="text-sm text-red-600">{errors.phone.message}</p>
        )}
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${formId}-schedule`}>¿Cuándo te llamamos?</Label>
        <select
          id={`${formId}-schedule`}
          className="w-full rounded-md border border-input bg-background p-2 text-base min-h-[44px]"
          aria-invalid={Boolean(errors.preferredSchedule)}
          {...register("preferredSchedule")}
        >
          {PREFERRED_SCHEDULES.map((schedule) => (
            <option key={schedule.value} value={schedule.value}>
              {schedule.label}
            </option>
          ))}
        </select>
        {errors.preferredSchedule && (
          <p className="text-sm text-red-600">
            {errors.preferredSchedule.message}
          </p>
        )}
      </div>

      <input
        type="text"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        {...register("website")}
      />

      <Button
        type="submit"
        disabled={isSubmitting}
        className="w-full min-h-[44px]"
      >
        {isSubmitting ? "Enviando…" : "Solicitar llamada"}
      </Button>
    </form>
  );
};

export default CallbackForm;